import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  IconButton,
  Chip,
  Badge,
  Tabs,
  Tab,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Paper
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PaymentIcon from '@mui/icons-material/Payment';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchNotifications, markAsRead } from '../features/notifications/notificationSlice';
import moment from 'moment';

// 通知類型圖標
const getNotificationIcon = (type) => {
  switch (type) {
    case 'announcement':
      return <AnnouncementIcon />;
    case 'payment':
      return <PaymentIcon />;
    case 'warning':
      return <WarningIcon color="error" />;
    case 'receipt':
      return <ReceiptIcon />;
    case 'maintenance':
      return <EventNoteIcon />;
    default:
      return <NotificationsIcon />;
  }
};

// 通知類型顏色
const getNotificationColor = (type) => {
  switch (type) {
    case 'announcement':
      return 'primary.main';
    case 'payment':
      return 'success.main';
    case 'warning':
      return 'error.main';
    case 'receipt':
      return 'info.main';
    case 'maintenance':
      return 'warning.main';
    default:
      return 'grey.500';
  }
};

// 通知項目元件
const NotificationItem = ({ notification, onView, onMarkAsRead }) => {
  const handleClick = () => {
    onView(notification);
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }
  };

  return (
    <ListItem 
      alignItems="flex-start" 
      sx={{ 
        bgcolor: notification.read ? 'transparent' : 'action.hover',
        '&:hover': { bgcolor: 'action.selected' },
        cursor: 'pointer'
      }}
      onClick={handleClick}
    >
      <ListItemAvatar>
        <Badge
          color="secondary"
          variant="dot"
          invisible={notification.read}
        >
          <Avatar sx={{ bgcolor: getNotificationColor(notification.type) }}>
            {getNotificationIcon(notification.type)}
          </Avatar>
        </Badge>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" component="span">
              {notification.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {moment(notification.timestamp).fromNow()}
            </Typography>
          </Box>
        }
        secondary={
          <React.Fragment>
            <Typography
              variant="body2"
              color="text.primary"
              component="span"
              sx={{ 
                display: 'inline',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
              }}
            >
              {notification.content}
            </Typography>
            {notification.label && (
              <Chip 
                label={notification.label} 
                size="small" 
                variant="outlined"
                sx={{ mt: 1, mr: 1 }}
              />
            )}
          </React.Fragment>
        }
      />
    </ListItem>
  );
};

const Notifications = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const dispatch = useDispatch();
  const { notifications, loading, error } = useSelector(state => state.notifications);
  
  useEffect(() => {
    dispatch(fetchNotifications());
  }, [dispatch]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleViewNotification = (notification) => {
    setSelectedNotification(notification);
    setDialogOpen(true);
  };
  
  const handleMarkAsRead = (notificationId) => {
    dispatch(markAsRead(notificationId));
  };
  
  const handleMarkAllAsRead = () => {
    const unreadNotifications = notifications
      .filter(n => !n.read)
      .map(n => n.id);
      
    unreadNotifications.forEach(id => {
      dispatch(markAsRead(id));
    });
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  // 過濾和分類通知
  const allNotifications = notifications || [];
  const unreadNotifications = allNotifications.filter(n => !n.read);
  const announcements = allNotifications.filter(n => n.type === 'announcement');
  const paymentNotifications = allNotifications.filter(n => n.type === 'payment');
  
  // 根據當前標籤顯示相應的通知
  const getFilteredNotifications = () => {
    switch (tabValue) {
      case 0: // 全部
        return allNotifications;
      case 1: // 未讀
        return unreadNotifications;
      case 2: // 公告
        return announcements;
      case 3: // 付款
        return paymentNotifications;
      default:
        return allNotifications;
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Paper sx={{ p: 2, bgcolor: '#fff9c4' }}>
          <Typography color="error">
            無法載入通知: {error}
          </Typography>
        </Paper>
      </Box>
    );
  }
  
  const filteredNotifications = getFilteredNotifications();
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">通知</Typography>
        {unreadNotifications.length > 0 && (
          <Button 
            variant="outlined" 
            size="small"
            onClick={handleMarkAllAsRead}
          >
            全部標記為已讀
          </Button>
        )}
      </Box>
      
      <Paper sx={{ mb: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography>全部</Typography>
                <Chip 
                  label={allNotifications.length} 
                  size="small" 
                  sx={{ ml: 1 }} 
                />
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography>未讀</Typography>
                <Chip 
                  label={unreadNotifications.length} 
                  size="small" 
                  color="error" 
                  sx={{ ml: 1 }} 
                  disabled={unreadNotifications.length === 0}
                />
              </Box>
            } 
            disabled={unreadNotifications.length === 0}
          />
          <Tab 
            label="公告" 
            disabled={announcements.length === 0}
          />
          <Tab 
            label="付款" 
            disabled={paymentNotifications.length === 0}
          />
        </Tabs>
      </Paper>
      
      <Card>
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {filteredNotifications.length > 0 ? (
            filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <NotificationItem 
                  notification={notification}
                  onView={handleViewNotification}
                  onMarkAsRead={handleMarkAsRead}
                />
                {index < filteredNotifications.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))
          ) : (
            <ListItem>
              <ListItemText 
                primary={
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    align="center"
                  >
                    沒有通知
                  </Typography>
                } 
              />
            </ListItem>
          )}
        </List>
      </Card>
      
      {/* 通知詳情對話框 */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        {selectedNotification && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: getNotificationColor(selectedNotification.type), mr: 1.5 }}>
                  {getNotificationIcon(selectedNotification.type)}
                </Avatar>
                <Typography variant="h6">
                  {selectedNotification.title}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="caption" color="text.secondary" paragraph>
                {moment(selectedNotification.timestamp).format('YYYY年MM月DD日 HH:mm')}
              </Typography>
              
              <Typography variant="body1" paragraph>
                {selectedNotification.content}
              </Typography>
              
              {selectedNotification.additionalInfo && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    詳細資訊:
                  </Typography>
                  <Typography variant="body2">
                    {selectedNotification.additionalInfo}
                  </Typography>
                </Box>
              )}
              
              {selectedNotification.actions && (
                <Box sx={{ mt: 3 }}>
                  {selectedNotification.actions.map((action, index) => (
                    <Button
                      key={index}
                      variant={action.primary ? "contained" : "outlined"}
                      color={action.color || "primary"}
                      sx={{ mr: 1, mb: 1 }}
                      onClick={handleCloseDialog}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>
                關閉
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Notifications; 